// Rendering an instant in a named zone.
//
// Every rendering carries its calendar date. This is not decoration: a single
// instant falls on different days in the two zones often enough that a bare
// time is actively misleading. Her Sunday 01:00 in Seoul is his Saturday 18:00
// in Copenhagen — same moment, different weekday, different date.

import { Temporal } from 'temporal-polyfill';

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const pad = (n) => String(n).padStart(2, '0');

/**
 * @typedef {object} Rendering
 * @property {string} zone      the IANA zone this was rendered in
 * @property {string} weekday   e.g. "Saturday"
 * @property {string} date      ISO calendar date, e.g. "2026-09-12"
 * @property {string} time      24-hour clock, e.g. "21:00"
 * @property {string} full      e.g. "Saturday 12 September 2026, 21:00"
 * @property {string} short     e.g. "Sat 12 Sep, 21:00"
 */

/**
 * Render a UTC instant in a named zone, always with its date.
 *
 * @param {string} instant ISO 8601 instant, e.g. "2026-09-12T12:00:00Z"
 * @param {string} zone    IANA zone identifier
 * @returns {Rendering}
 */
export function renderInZone(instant, zone) {
  const zoned = Temporal.Instant.from(instant).toZonedDateTimeISO(zone);
  const weekday = WEEKDAYS[zoned.dayOfWeek - 1];
  const month = MONTHS[zoned.month - 1];
  const time = `${pad(zoned.hour)}:${pad(zoned.minute)}`;
  const date = `${zoned.year}-${pad(zoned.month)}-${pad(zoned.day)}`;

  const hour12 = zoned.hour % 12 === 0 ? 12 : zoned.hour % 12;
  const meridiem = zoned.hour < 12 ? 'am' : 'pm';

  return {
    zone,
    weekday,
    date,
    time,
    /** 12-hour form, e.g. "9:00 pm". The 24-hour `time` stays authoritative. */
    time12: `${hour12}:${pad(zoned.minute)} ${meridiem}`,
    full: `${weekday} ${zoned.day} ${month} ${zoned.year}, ${time}`,
    short: `${weekday.slice(0, 3)} ${zoned.day} ${month.slice(0, 3)}, ${time}`,
  };
}

/**
 * Render one instant in both participants' zones, for the summary and the
 * success screen.
 *
 * @param {string} instant
 * @param {string} herZone
 * @param {string} homeZone
 * @returns {{hers: Rendering, home: Rendering, differentDay: boolean}}
 */
export function renderBothClocks(instant, herZone, homeZone) {
  const hers = renderInZone(instant, herZone);
  const home = renderInZone(instant, homeZone);
  return { hers, home, differentDay: hers.date !== home.date };
}
